import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Button from '../components/Button';
import Input from '../components/Input';
import styles from './Auth.module.css';
// 0.36.733 — On the admin subdomain, successful login redirects to
// /admin instead of /dashboard so admins land in the admin shell
// directly rather than the citizen dashboard.
import { isAdminHost } from '../utils/adminHost';

export default function Login() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  /* 0.42.05 - Only offer the card route when it can actually work.
   *
   * The link was unconditional, so a citizen was invited into a flow
   * that refuses every attempt - and on this deployment the button was
   * showing while no authority was configured at all.
   *
   * Defaults to HIDDEN until the answer arrives: a button that appears
   * and then vanishes is worse than one that appears a moment late. */
  const [idCardAvailable, setIdCardAvailable] = useState(false);
  /* 0.46.72 - With the card-only address set up, the button goes
   * straight there: the browser asks for the card on this click only. */
  const [cardPort, setCardPort] = useState(null);
  const cardHref = cardPort
    ? `https://${window.location.hostname}:${cardPort}/api/auth/id-card/begin?next=${encodeURIComponent('/login/id-card')}`
    : null;
  useEffect(() => {
    fetch('/api/auth/id-card/status', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { available: false }))
      .then(d => { setIdCardAvailable(!!d.available); setCardPort(d.card_port || null); })
      .catch(() => setIdCardAvailable(false));
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 0.36.201 — When the server signals a login-method block (password
  // correct but the admin set this user's policy to a different
  // method), we show a redirect message + CTA in place of the error
  // box. State carries the allowed_method so the CTA can route to
  // the right surface.
  const [blockedMethod, setBlockedMethod] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBlockedMethod(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      // 0.36.201 — Login policy block. Backend returned 200 with a
      // structured response indicating the user's login_method
      // doesn't permit password sign-in. Currently only
      // 'id_card_only' is enforced (verification_code is deferred
      // until that endpoint is built).
      if (result && result.login_method_blocked) {
        setBlockedMethod(result.allowed_method);
        return;
      }
      // 0.36.733 — Default post-login destination is hostname-aware:
      // on the admin subdomain, land in the admin shell (/admin);
      // on the citizen site, land on the citizen dashboard. An
      // explicit `from` (deep link that bounced through login)
      // always wins over the default.
      const defaultDest = isAdminHost() ? '/admin' : '/dashboard';
      // If backend returned a 2FA challenge, navigate to the verify page,
      // passing the challenge info in router state.
      if (result && result.requires_2fa) {
        navigate('/login/verify', {
          state: {
            challenge_token: result.challenge_token,
            email_masked: result.email_masked,
            expires_in_seconds: result.expires_in_seconds,
            redirect_to: location.state?.from?.pathname
              || (() => {
                const n = new URLSearchParams(location.search).get('next');
                return n && n.startsWith('/') && !n.startsWith('//') ? n : null;
              })()
              || defaultDest,
          },
          replace: true,
        });
        return;
      }
      /* 0.41.98 - `?next=` as well as router state.
       *
       * A session expiring mid-edit is a full page redirect from the
       * api layer, which cannot set router state - so without this the
       * admin signs in and lands on the admin home, having lost both
       * their work and their place.
       *
       * Only same-origin paths are honoured: a `next` pointing
       * anywhere else would make the login page an open redirect,
       * which is a phishing tool. */
      const nextParam = new URLSearchParams(location.search).get('next');
      const safeNext = nextParam && nextParam.startsWith('/')
        && !nextParam.startsWith('//') ? nextParam : null;
      const dest = location.state?.from?.pathname || safeNext || defaultDest;
      navigate(dest, { replace: true });
    } catch (err) {
      if (err.status === 403 && err.message === 'pending_approval') {
        // 0.48.26 - Not approved yet.
        setError(t('login.pending_approval'));
      } else if (err.status === 423) {
        setError(err.message || t('login.error_locked'));
      } else if (err.status === 401) {
        setError(t('login.error_invalid'));
      } else {
        setError(t('login.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={`${styles.page} main`}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('login.title')}</h1>
        <p className={styles.subtitle}>{t('login.subtitle')}</p>
        {error && <div className={styles.errorBox} role="alert">{error}</div>}
        {/* 0.36.201 — Login policy redirect. Shown when the server
          * confirmed the password but the user's login_method
          * doesn't permit password sign-in. Currently only
          * 'id_card_only' surfaces here. Reuses the errorBox style
          * so the visual treatment matches other error states. */}
        {blockedMethod === 'id_card_only' && (
          <div className={styles.errorBox} role="alert">
            <div style={{ marginBottom: 12 }}>
              {t('login.use_id_card.message')}
            </div>
            <Link to="/login/id-card" className={styles.inlineLink}>
              {t('login.use_id_card.cta')}
            </Link>
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <Input
            label={t('login.email')} type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label={t('login.password')} type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <div className={styles.forgotRow}>
            <Link to="/forgot-password" className={styles.inlineLink}>
              {t('login.forgot_password')}
            </Link>
          </div>
          <Button type="submit" block large loading={loading}>{t('login.submit')}</Button>
        </form>
        {/* 0.42.05 - The "or" goes with the button it separates. Left
            alone it would sit under the form with nothing after it,
            which reads as something failing to load. */}
        {idCardAvailable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                      margin: 'var(--space-4) 0', color: 'var(--color-text-600)',
                      fontSize: 'var(--font-size-sm)' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--color-text-300)' }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--color-text-300)' }} />
        </div>
        )}
        {idCardAvailable && (() => {
          const look = {
            display: 'block', textAlign: 'center', padding: 'var(--space-3)',
            border: '1px solid var(--color-text-300)', borderRadius: 'var(--radius-sm)',
            color: 'var(--color-text-800)', textDecoration: 'none',
            fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)' };
          return cardHref
            ? <a href={cardHref} style={look}>Log in with ID card</a>
            : <Link to="/login/id-card" style={look}>Log in with ID card</Link>;
        })()}
        <p className={styles.altLink}>
          {t('login.no_account')}<Link to="/signup">{t('login.signup_link')}</Link>
        </p>
      </div>
    </main>
  );
}
